import { useEffect } from "preact/hooks";
import { DataTable, DetailLayout, EmptyState, FigureStrip, fmtNum, ListToolbar, PageHeader, PillGroup, RailPanel } from "../../../../ui/src/index.ts";
import type { TableColumn } from "../../../../ui/src/index.ts";
import { activityByIntent, isLive, type IntentActivity } from "../../domain/activity.ts";
import { DEMAND_DAYS, DEMAND_GROUPS, demandRows, type DemandGroup } from "../../domain/demand.ts";
import { perMillion, usd } from "../../domain/money.ts";
import type { DemandItem, HubSummary } from "../../domain/types.ts";
import { loadDemand, loadEvents } from "../actions.ts";
import { Failed, Loading } from "../parts.tsx";
import { href, navigate, type Route } from "../router.ts";
import { useStore } from "../store.ts";
import { capitalize, day, GROUP_WORDS } from "../words.ts";
import { HeaderActions } from "../Guide.tsx";

type DemandRoute = Extract<Route, { name: "demand" }>;

export function Demand({ hub, route }: { hub: HubSummary; route: DemandRoute }) {
  const demand = useStore((s) => s.demand[`${hub.hub.id}:${route.days}`]);
  const events = useStore((s) => s.events[hub.hub.id]);
  useEffect(() => { void loadDemand(hub.hub.id, route.days); void loadEvents(hub.hub.id); }, [hub.hub.id, route.days]);
  const go = (patch: Partial<DemandRoute>, replace = false) => navigate({ ...route, ...patch }, replace);

  const activity = events?.status === "ok" ? activityByIntent(events.value.events) : new Map<string, IntentActivity>();
  const planned = hub.manifest.intents.filter((i) => !isLive(i)).map((i) => ({ intent: i, count: activity.get(i.id)?.byKey.planned ?? 0 })).filter((p) => p.count > 0).sort((a, b) => b.count - a.count);
  const columns: TableColumn<DemandItem>[] = [
    { id: "ask", label: "Ask", fill: true, resizable: true, render: (d) => <span class="st-ask st-cell-text" title={d.question}>{d.question}</span> },
    { id: "reason", label: route.group === "declined" ? "Why it was declined" : "Why it wasn't dispatched", fill: true, resizable: true, render: (d) => (d.reason ? <span class="st-note st-cell-text st-reason" title={d.reason}>{d.reason}</span> : <span class="st-note">–</span>) },
    { id: "count", label: "Asks", numeric: true, render: (d) => fmtNum(d.count) },
    { id: "bid", label: "Bid", numeric: true, render: (d) => perMillion(d.bidUsd * 1e6) },
    { id: "last", label: "Last asked", numeric: true, render: (d) => <span class="st-note">{day(d.lastAt)}</span> },
  ];

  let body;
  if (!demand || demand.status === "loading") body = <Loading label="Loading demand" />;
  else if (demand.status === "failed") body = <Failed what="the demand" notFound="this hub" error={demand.error} retry={() => void loadDemand(hub.hub.id, route.days, true)} />;
  else {
    const d = demand.value;
    const rows = demandRows(d, route.group, route.search);
    body = <>
      <div class="st-summary"><FigureStrip label={`${GROUP_WORDS[route.group]} asks`} figures={[
        { label: GROUP_WORDS[route.group], value: fmtNum(d[route.group].questions), context: `${fmtNum(d[route.group].distinct)} distinct asks · last ${route.days} days` },
        ...(route.group === "declined" ? [{ label: "Offered", value: usd(d.declined.bidUsd), context: "total bids on declined asks" }] : []),
      ]} /></div>
      <div class="st-demand-groups">
        <PillGroup label="Demand group" value={route.group} onChange={(group: DemandGroup) => go({ group, search: "" })}
          options={DEMAND_GROUPS.map((g) => ({ value: g, label: `${GROUP_WORDS[g]} ${fmtNum(d[g].questions)}` }))} />
      </div>
      <div data-tour="asks"><ListToolbar class="st-demand-toolbar" searchValue={route.search} onSearchChange={(search) => go({ search }, true)} searchPlaceholder="Find an ask"
        count={`${fmtNum(rows.length)} of ${fmtNum(d[route.group].distinct)} distinct asks`} />
      {rows.length ? <DataTable caption={`${GROUP_WORDS[route.group]} asks, last ${route.days} days`} columns={columns} rows={rows} rowKey={(r) => r.question} storageKey="studio-demand" />
        : <EmptyState>{route.search ? "No asks match this search." : `No ${GROUP_WORDS[route.group].toLowerCase()} asks in the last ${route.days} days.`}</EmptyState>}</div>
    </>;
  }

  return <>
    <PageHeader title={<>Understand your <strong>demand</strong></>} description="Review the asks your hub didn't answer and why they weren't dispatched or were declined."
      action={<HeaderActions><span data-tour="period"><PillGroup label="Period" value={String(route.days)} onChange={(v: string) => go({ days: Number(v) })} options={DEMAND_DAYS.map((n) => ({ value: String(n), label: `${n} days` }))} /></span></HeaderActions>} />
    <DetailLayout rail={<>
      <RailPanel title="Planned questions people asked" class="st-planned">
        {planned.length ? <><ul class="st-plain">{planned.map((p) => <li key={p.intent.id}><a href={href({ name: "question", hub: hub.hub.id, intent: p.intent.id })}>{capitalize(p.intent.label)}</a><b>{fmtNum(p.count)}</b></li>)}</ul><p class="st-rail-note">From the newest events</p></>
          : <p>None in the newest events.</p>}
      </RailPanel>
    </>}>{body}</DetailLayout>
  </>;
}
